import { Vector3 } from 'three';
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';
import { updateAllMovementsDatas } from "../Movement/ToothMovementTracker";
import gumDeformationManager from 'Domain/Scene/Manager/GumDeformationManager';
import toothOffsetManager from 'Domain/Scene/Manager/ToothOffsetManager';
import { undoLastToothAction, redoLastToothAction, resetToothHistory } from './ToothSaveHandler';

const GUM_MOVEMENT_TYPES = {
    MIXED: -1,
    STANDARD: 0,
    VERTICAL_TRANSLATION: 1,
    ROTATION_RIDGE_AXIS: 2,
    ROTATION_ROOT_AXIS: 3,
    OTHER_ROTATION: 4
};

/* ──────────────────────────────────────────────
   TOOTH POSITION MANIPULATION
   ────────────────────────────────────────────── */

export const applyMatrixToToothPivot = (tooth, matrix) => {
    tooth.pivot.matrix.copy(matrix);
    tooth.pivot.matrix.decompose(tooth.pivot.position, tooth.pivot.quaternion, tooth.pivot.scale);
    tooth.updateWorldPosition();
    tooth.offsetRefObj();
};

export const setPivotOnBarycenter = (tooth = meshSelectionStore.getClickedTooth()) => {
    if (!tooth) return;
    const baryCenterWorldPosition = new Vector3();
    tooth.baryCenter.getWorldPosition(baryCenterWorldPosition);
    tooth.updateWorldPosition();
    tooth.pivot.position.copy(baryCenterWorldPosition);
    tooth.offsetRefObj();
};

export const resetSelectedToothToInitialPosition = () => {
    const selectedTooth = meshSelectionStore.getClickedTooth();
    if (!selectedTooth || selectedTooth.isStepInit || selectedTooth.movementActual <= 0) return;

    const previousMatrix = selectedTooth.pivot.matrix.clone();
    const initialMatrix = selectedTooth.mouvements[0];
    applyMatrixToToothPivot(selectedTooth, initialMatrix);
    updateAllMovementsDatas(selectedTooth);
    selectedTooth.isStepInit = true;
    selectedTooth.movementActual = 0;

    const toothId = selectedTooth.refObj?.name?.split('_').pop();
    if (toothId) resetToothHistory(toothId);

    gumDeformationManager.deformGumForTooth(selectedTooth, previousMatrix, GUM_MOVEMENT_TYPES.MIXED);
};

export const restoreSelectedToothToNextPosition = async () => {
    const selectedTooth = meshSelectionStore.getClickedTooth();
    if (!selectedTooth) return;

    const previousMatrix = selectedTooth.pivot.matrix.clone();
    const nextMatrix = getNextMovementMatrix();

    applyMatrixToToothPivot(selectedTooth, nextMatrix);
    updateAllMovementsDatas(selectedTooth);

    if (!selectedTooth.isStepInit) selectedTooth.movementActual++;
    selectedTooth.isStepInit = false;

    const toothId = selectedTooth.refObj?.name?.split('_').pop();
    if (toothId) redoLastToothAction(toothId);

    gumDeformationManager.deformGumForTooth(selectedTooth, previousMatrix, GUM_MOVEMENT_TYPES.MIXED);
};

const getNextMovementMatrix = () => {
    const selectedTooth = meshSelectionStore.getClickedTooth();
    if (!selectedTooth) return null;
    const movementIndex = selectedTooth.isStepInit
        ? selectedTooth.movementActual
        : selectedTooth.movementActual + 1;
    return selectedTooth.mouvements[movementIndex];
};

export const undoSelectedToothLastMovement = () => {
    const selectedTooth = meshSelectionStore.getClickedTooth();
    if (!selectedTooth || selectedTooth.movementActual <= 0) return;

    const previousMatrix = selectedTooth.pivot.matrix.clone();
    const previousStateMatrix = selectedTooth.mouvements[selectedTooth.movementActual - 1];
    applyMatrixToToothPivot(selectedTooth, previousStateMatrix);
    selectedTooth.movementActual--;
    if (selectedTooth.movementActual <= 0) selectedTooth.isStepInit = true;
    updateAllMovementsDatas(selectedTooth);

    const toothId = selectedTooth.refObj?.name?.split('_').pop();
    if (toothId) undoLastToothAction(toothId);

    gumDeformationManager.deformGumForTooth(selectedTooth, previousMatrix, GUM_MOVEMENT_TYPES.MIXED);
};

export const resetAllTeethToInitialPosition = (globalTeethObjects) => {
    Object.values(globalTeethObjects).forEach(resetToothToInitialPositionIfMoved);
};

export const hasAnyToothBeenModified = (globalTeethObjects) =>
    Object.values(globalTeethObjects).some(tooth => !tooth.isStepInit);

const resetToothToInitialPositionIfMoved = (tooth) => {
    if (tooth.mouvements.length <= 1) return;
    const initialMatrix = tooth.mouvements[0];
    applyMatrixToToothPivot(tooth, initialMatrix);
    tooth.isStepInit = true;
    tooth.movementActual = 0;
    updateAllMovementsDatas(tooth);

    const toothId = tooth.refObj?.name?.split('_').pop();
    if (toothId) resetToothHistory(toothId);
};

export const applyOffsetToSelectedToothKeepIndices = async () => {
    try {
        await toothOffsetManager.applyOffsetToSelectedToothKeepIndices();
    } catch (error) {
        console.warn('Offset application (keep indices) failed:', error.message);
    }
};
